import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Input, InputNumber, message, Modal, Select, Space, Switch, Table, Tabs, Tag } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useModulePermission } from '../../hooks/usePermission';
import {
  chain29VoucherApi, FormalVoucherHeader, SaveVoucherLine, TemporaryVoucherHeader, VoucherLine,
} from '../../services/chain29VoucherApi';
import {
  AcceptanceDataPanel,
  AcceptanceFieldHint,
  AcceptanceToolbar,
  buildChain29FormulaEvidence,
  chain29VoucherAcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';

const today = () => dayjs().format('YYYY-MM-DD');
const emptyLine = (): SaveVoucherLine => ({ account: '', side: 'D', amount: 0, summary: '', department: '' });
const errorText = (error: any) => error?.response?.data?.error || error?.message || '作業失敗';
const isTemporaryUnconfirmed = (status: string) => !['P', 'X', 'C'].includes((status || '').trim().toUpperCase());

const Chain29VoucherForm: React.FC = () => {
  const { isEnabled } = useAcceptanceMode();
  const acceptanceEnabled = isEnabled(chain29VoucherAcceptanceManifest.formId);
  const tempPerm = useModulePermission('acc_tempvoucher');
  const formalPerm = useModulePermission('acc_vouchers');
  const [activeTab, setActiveTab] = useState('temporary');
  const [loading, setLoading] = useState(false);
  const [tempStatus, setTempStatus] = useState<'unconfirmed' | 'confirmed' | 'voided'>('unconfirmed');
  const [temps, setTemps] = useState<TemporaryVoucherHeader[]>([]);
  const [formals, setFormals] = useState<FormalVoucherHeader[]>([]);
  const [selectedTemps, setSelectedTemps] = useState<React.Key[]>([]);
  const [confirmDate, setConfirmDate] = useState(today());
  const [mergeByDate, setMergeByDate] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailLines, setDetailLines] = useState<VoucherLine[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editTemp, setEditTemp] = useState<{ number: string; line: SaveVoucherLine & { sequence: number } } | null>(null);
  const [formalEditor, setFormalEditor] = useState<{ code?: string; voucherDate: string; details: SaveVoucherLine[] } | null>(null);
  const [acceptanceDocumentKey, setAcceptanceDocumentKey] = useState<string>();
  const [acceptanceKind, setAcceptanceKind] = useState<'temporary' | 'formal'>();
  const [acceptanceLastApiResponse, setAcceptanceLastApiResponse] = useState<any>(null);
  const [acceptanceDatabaseRecord, setAcceptanceDatabaseRecord] = useState<any>(null);
  const [acceptanceAudit, setAcceptanceAudit] = useState<unknown[]>([]);
  const [acceptanceErrors, setAcceptanceErrors] = useState<string[]>([]);
  const [acceptanceLastMutation, setAcceptanceLastMutation] = useState<unknown>(null);
  const H = (fieldId: string) => <AcceptanceFieldHint
    manifest={chain29VoucherAcceptanceManifest}
    fieldId={fieldId}
    documentKey={acceptanceDocumentKey}
  />;

  const loadTemporary = useCallback(async () => {
    if (!tempPerm.canView) return;
    setLoading(true);
    try {
      const result = await chain29VoucherApi.listTemporary(tempStatus);
      setTemps(result.items || []);
      setSelectedTemps([]);
    } catch (error) { message.error(errorText(error)); } finally { setLoading(false); }
  }, [tempPerm.canView, tempStatus]);

  const loadFormal = useCallback(async () => {
    if (!formalPerm.canView) return;
    setLoading(true);
    try { setFormals((await chain29VoucherApi.listFormal()).items || []); }
    catch (error) { message.error(errorText(error)); } finally { setLoading(false); }
  }, [formalPerm.canView]);

  useEffect(() => { loadTemporary(); }, [loadTemporary]);
  useEffect(() => { loadFormal(); }, [loadFormal]);

  const showTemporary = async (row: TemporaryVoucherHeader) => {
    try {
      const result = await chain29VoucherApi.getTemporary(row.number);
      if (acceptanceEnabled) {
        const fresh = await chain29VoucherApi.getTemporary(row.number);
        setAcceptanceDocumentKey(row.number);
        setAcceptanceKind('temporary');
        setAcceptanceLastApiResponse(result);
        setAcceptanceDatabaseRecord(fresh);
        setAcceptanceAudit([]);
        setAcceptanceErrors([]);
      }
      setDetailTitle(`暫存傳票 ${row.number}`); setDetailLines(result.details); setDetailOpen(true);
    } catch (error) {
      setAcceptanceErrors(['暫存傳票 API／DB 即時重讀失敗']);
      message.error(errorText(error));
    }
  };
  const showFormal = async (row: FormalVoucherHeader) => {
    try {
      const result = await chain29VoucherApi.getFormal(row.code);
      if (acceptanceEnabled) {
        const [fresh, audit] = await Promise.all([
          chain29VoucherApi.getFormal(row.code),
          chain29VoucherApi.listFormalAudit(row.code),
        ]);
        setAcceptanceDocumentKey(row.code);
        setAcceptanceKind('formal');
        setAcceptanceLastApiResponse(result);
        setAcceptanceDatabaseRecord(fresh);
        setAcceptanceAudit(audit.map(entry => ({
          ...entry,
          form_id: chain29VoucherAcceptanceManifest.formId,
          doc_key: row.code,
        })));
        setAcceptanceErrors([]);
      }
      setDetailTitle(`正式傳票 ${row.code}`); setDetailLines(result.details); setDetailOpen(true);
    } catch (error) {
      setAcceptanceErrors(['正式傳票 API／DB 即時重讀或 Audit 讀取失敗']);
      message.error(errorText(error));
    }
  };

  const confirmSelected = () => {
    if (!selectedTemps.length) return;
    Modal.confirm({
      title: `確認拋轉 ${selectedTemps.length} 張暫存傳票？`,
      content: mergeByDate ? '將依各暫存傳票日期分組產生正式傳票。' : `會計日期：${confirmDate}`,
      onOk: async () => {
        try {
          const response = await chain29VoucherApi.formalizeBatch(selectedTemps.map(String), confirmDate, mergeByDate);
          setAcceptanceLastMutation(response);
          message.success('暫存傳票已拋轉'); await Promise.all([loadTemporary(), loadFormal()]);
        } catch (error) { message.error(errorText(error)); }
      },
    });
  };

  const voidTemporary = (row: TemporaryVoucherHeader) => Modal.confirm({
    title: `作廢暫存傳票 ${row.number}？`,
    onOk: async () => {
      try { await chain29VoucherApi.voidTemporary(row.number); message.success('已作廢'); await loadTemporary(); }
      catch (error) { message.error(errorText(error)); }
    },
  });

  const beginTempEdit = async (row: TemporaryVoucherHeader) => {
    try {
      const result = await chain29VoucherApi.getTemporary(row.number);
      const source = result.details[0];
      if (!source) return message.warning('沒有可修改明細');
      setEditTemp({ number: row.number, line: {
        sequence: source.sequence, department: source.department, account: source.account,
        side: source.side === 'C' ? 'C' : 'D', amount: source.amount, summary: source.summary,
        writeOffCode1: source.writeOffCode1 || '', writeOffCode2: source.writeOffCode2 || '',
      } });
    } catch (error) { message.error(errorText(error)); }
  };

  const saveTempEdit = async () => {
    if (!editTemp) return;
    try {
      const { sequence, ...line } = editTemp.line;
      await chain29VoucherApi.editTemporaryDetail(editTemp.number, sequence, line);
      message.success('明細與借貸總額已更新'); setEditTemp(null); await loadTemporary();
    } catch (error) { message.error(errorText(error)); }
  };

  const openFormalEdit = async (row?: FormalVoucherHeader) => {
    if (!row) return setFormalEditor({ voucherDate: today(), details: [emptyLine(), { ...emptyLine(), side: 'C' }] });
    try {
      const result = await chain29VoucherApi.getFormal(row.code);
      setFormalEditor({ code: row.code, voucherDate: row.voucherDate, details: result.details.map(line => ({
        account: line.account, accountName: line.accountName, taxAccount: line.taxAccount,
        taxAccountName: line.taxAccountName, side: line.side === 'C' ? 'C' : 'D', amount: line.amount,
        summary: line.summary, department: line.department, departmentName: line.departmentName,
      })) });
    } catch (error) { message.error(errorText(error)); }
  };

  const formalTotals = useMemo(() => {
    const details = formalEditor?.details || [];
    return {
      debit: details.filter(x => x.side === 'D').reduce((sum, x) => sum + Number(x.amount || 0), 0),
      credit: details.filter(x => x.side === 'C').reduce((sum, x) => sum + Number(x.amount || 0), 0),
    };
  }, [formalEditor]);

  const patchFormalLine = (index: number, patch: Partial<SaveVoucherLine>) => setFormalEditor(current => current && ({
    ...current, details: current.details.map((line, i) => i === index ? { ...line, ...patch } : line),
  }));
  const saveFormal = async () => {
    if (!formalEditor) return;
    if (!formalEditor.details.length || Math.round(formalTotals.debit * 100) !== Math.round(formalTotals.credit * 100)) {
      return message.warning('借貸必須平衡且至少有一筆明細');
    }
    try {
      const response = formalEditor.code
        ? await chain29VoucherApi.updateFormal(formalEditor.code, formalEditor.voucherDate, formalEditor.details)
        : await chain29VoucherApi.createFormal(formalEditor.voucherDate, formalEditor.details);
      setAcceptanceLastMutation(response);
      message.success('正式傳票已儲存'); setFormalEditor(null); await loadFormal();
    } catch (error) { message.error(errorText(error)); }
  };
  const voidFormal = (row: FormalVoucherHeader) => Modal.confirm({
    title: `作廢正式傳票 ${row.code}？`, content: '明細與稽核歷程會保留，不會刪除。',
    onOk: async () => {
      try { await chain29VoucherApi.voidFormal(row.code); message.success('正式傳票已作廢'); await loadFormal(); }
      catch (error) { message.error(errorText(error)); }
    },
  });

  const formulaEvidence = useMemo(() => {
    const response = acceptanceLastApiResponse as { header?: FormalVoucherHeader | TemporaryVoucherHeader; details?: VoucherLine[] } | null;
    const database = acceptanceDatabaseRecord as { header?: FormalVoucherHeader | TemporaryVoucherHeader; details?: VoucherLine[] } | null;
    const details = formalEditor?.details || response?.details || [];
    return buildChain29FormulaEvidence(chain29VoucherAcceptanceManifest, {
      kind: formalEditor ? 'formal' : acceptanceKind,
      details,
      apiHeader: response?.header,
      databaseHeader: database?.header,
      lastMutationResponse: acceptanceLastMutation,
    });
  }, [acceptanceDatabaseRecord, acceptanceKind, acceptanceLastApiResponse, acceptanceLastMutation, formalEditor]);

  const acceptanceAutoFill = useCallback(() => {
    setActiveTab('formal');
    setFormalEditor({
      voucherDate: today(),
      details: [
        { department: 'D001', account: '1101', side: 'D', amount: 100, summary: '驗收模式借方範例' },
        { department: 'D001', account: '2101', side: 'C', amount: 100, summary: '驗收模式貸方範例' },
      ],
    });
    setAcceptanceDocumentKey(undefined);
    setAcceptanceKind('formal');
    setAcceptanceLastApiResponse(null);
    setAcceptanceDatabaseRecord(null);
    setAcceptanceAudit([]);
    setAcceptanceErrors([]);
    message.success('已建立未儲存的平衡傳票草稿；儲存仍走原本 CHAIN-29 API。');
  }, []);

  const detailColumns = [
    { title: <>項次{H('f30e1_02')}{H('f312_02')}</>, dataIndex: 'sequence', width: 85 },
    { title: <>部門{H('f30e1_51')}{H('f312_51')}</>, dataIndex: 'department', width: 100 },
    { title: <>科目{H('f30e1_11')}{H('f312_11')}</>, dataIndex: 'account', width: 120 },
    { title: '科目名稱', dataIndex: 'accountName', width: 150 },
    { title: <>稅務科目{H('f30e1_111')}{H('f312_111')}</>, dataIndex: 'taxAccount', width: 120 },
    { title: <>借貸{H('f30e1_12')}{H('f312_12')}</>, dataIndex: 'side', width: 85 },
    { title: <>金額{H('f30e1_13')}{H('f312_13')}</>, dataIndex: 'amount', align: 'right' as const },
    { title: <>摘要{H('f30e1_14')}{H('f312_14')}</>, dataIndex: 'summary' },
    { title: <>來源暫存{H('f312_23')}</>, dataIndex: 'temporaryVoucher', width: 130 },
  ];
  const tempColumns = [
    { title: <>暫存傳票{H('f30e_01')}</>, dataIndex: 'number' },
    { title: <>正式傳票{H('f30e_011')}</>, dataIndex: 'formal' },
    { title: <>日期{H('f30e_25')}</>, dataIndex: 'voucherDate' },
    { title: <>來源類型{H('f30e_22')}</>, dataIndex: 'sourceType' },
    { title: <>來源單號{H('f30e_23')}</>, dataIndex: 'sourceNumber' },
    { title: <>筆數{H('f30e_14')}</>, dataIndex: 'detailCount' },
    { title: <>借方{H('f30e_15')}</>, dataIndex: 'debitTotal', align: 'right' as const },
    { title: <>貸方{H('f30e_16')}</>, dataIndex: 'creditTotal', align: 'right' as const },
    { title: <>狀態{H('f30e_80')}</>, render: (_: any, row: TemporaryVoucherHeader) => <Tag>{row.status || 'A'}</Tag> },
    { title: '操作', render: (_: any, row: TemporaryVoucherHeader) => <Space>
      <Button size="small" onClick={() => showTemporary(row)}>明細</Button>
      <Button data-acceptance-action="C29-ACTION-EDIT-TEMP" size="small" disabled={!tempPerm.canEdit || !isTemporaryUnconfirmed(row.status) || row.debitTotal === row.creditTotal} onClick={() => beginTempEdit(row)}>修正</Button>
      <Button data-acceptance-action="C29-ACTION-VOID-TEMP" size="small" danger disabled={!tempPerm.canApprove || !isTemporaryUnconfirmed(row.status)} onClick={() => voidTemporary(row)}>作廢</Button>
    </Space> },
  ];
  const formalColumns = [
    { title: <>正式傳票{H('f311_01')}</>, dataIndex: 'code' },
    { title: <>日期{H('f311_11')}</>, dataIndex: 'voucherDate' },
    { title: <>筆數{H('f311_14')}</>, dataIndex: 'detailCount' },
    { title: <>借方{H('f311_15')}</>, dataIndex: 'debitTotal', align: 'right' as const },
    { title: <>貸方{H('f311_16')}</>, dataIndex: 'creditTotal', align: 'right' as const },
    { title: <>確認人{H('f311_81')}</>, dataIndex: 'confirmUser' },
    { title: <>確認日{H('f311_82')}</>, dataIndex: 'confirmDate' },
    { title: <>狀態{H('f311_80')}</>, render: (_: any, row: FormalVoucherHeader) => <Tag color={row.status === 'X' ? 'red' : 'green'}>{row.status}</Tag> },
    { title: '操作', render: (_: any, row: FormalVoucherHeader) => <Space>
      <Button size="small" onClick={() => showFormal(row)}>明細</Button>
      <Button data-acceptance-action="C29-ACTION-SAVE-FORMAL" size="small" disabled={!formalPerm.canEdit || row.status !== 'A'} onClick={() => openFormalEdit(row)}>修改</Button>
      <Button data-acceptance-action="C29-ACTION-VOID-FORMAL" size="small" danger disabled={!formalPerm.canApprove || row.status !== 'A'} onClick={() => voidFormal(row)}>作廢</Button>
    </Space> },
  ];

  const temporaryPanel = !tempPerm.canView ? <Alert type="warning" message="沒有暫存傳票查詢權限" /> : <>
    <Space wrap style={{ marginBottom: 12 }}>
      <Select value={tempStatus} onChange={setTempStatus} options={[
        { value: 'unconfirmed', label: '未確認' }, { value: 'confirmed', label: '已確認' }, { value: 'voided', label: '已作廢' },
      ]} />
      <DatePicker value={dayjs(confirmDate)} onChange={value => setConfirmDate(value?.format('YYYY-MM-DD') || '')} disabled={mergeByDate} />
      <Switch checked={mergeByDate} onChange={setMergeByDate} checkedChildren="依原日期分組" unCheckedChildren="指定日期合併" />
      <Button data-acceptance-action="C29-ACTION-FORMALIZE" type="primary" disabled={!tempPerm.canApprove || !selectedTemps.length || (!mergeByDate && !confirmDate)} onClick={confirmSelected}>拋轉正式傳票</Button>
      <Button icon={<ReloadOutlined />} onClick={loadTemporary}>重新整理</Button>
    </Space>
    <Table loading={loading} rowKey="number" dataSource={temps} columns={tempColumns} pagination={{ pageSize: 20 }}
      rowSelection={tempStatus === 'unconfirmed' ? { selectedRowKeys: selectedTemps, onChange: setSelectedTemps } : undefined} />
  </>;
  const formalPanel = !formalPerm.canView ? <Alert type="warning" message="沒有正式傳票查詢權限" /> : <>
    <Space style={{ marginBottom: 12 }}>
      <Button data-acceptance-action="C29-ACTION-SAVE-FORMAL" type="primary" icon={<PlusOutlined />} disabled={!formalPerm.canCreate} onClick={() => openFormalEdit()}>新增正式傳票</Button>
      <Button icon={<ReloadOutlined />} onClick={loadFormal}>重新整理</Button>
    </Space>
    <Table loading={loading} rowKey="code" dataSource={formals} columns={formalColumns} pagination={{ pageSize: 20 }} />
  </>;

  return <div style={{ padding: 20 }}>
    <h2>CHAIN-29 會計傳票鏈</h2>
    <Alert style={{ marginBottom: 12 }} type="info" showIcon message="v1 採本位幣與 FoxPro F312 欄位；多幣別欄位尚未開放。" />
    <AcceptanceToolbar
      manifest={chain29VoucherAcceptanceManifest}
      onAutoFill={acceptanceAutoFill}
      documentKey={acceptanceDocumentKey}
      autoFillReason="建立未儲存的本位幣借貸平衡草稿；不會直接寫入資料庫"
    />
    <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
      { key: 'temporary', label: '暫存傳票確認', children: temporaryPanel },
      { key: 'formal', label: '正式會計傳票', children: formalPanel },
    ]} />

    <Modal title={detailTitle} open={detailOpen} onCancel={() => setDetailOpen(false)} footer={null} width={950}>
      <Table rowKey="sequence" dataSource={detailLines} columns={detailColumns} pagination={false} size="small" />
    </Modal>

    <Modal title={editTemp ? `修正 ${editTemp.number} 第 ${editTemp.line.sequence} 筆` : ''} open={!!editTemp}
      onCancel={() => setEditTemp(null)} onOk={saveTempEdit} okText="儲存修正">
      {editTemp && <Space direction="vertical" style={{ width: '100%' }}>
        <Input addonBefore="部門" value={editTemp.line.department} maxLength={4} onChange={e => setEditTemp({ ...editTemp, line: { ...editTemp.line, department: e.target.value } })} />
        <Input addonBefore="科目" value={editTemp.line.account} maxLength={12} onChange={e => setEditTemp({ ...editTemp, line: { ...editTemp.line, account: e.target.value } })} />
        <Select value={editTemp.line.side} style={{ width: '100%' }} onChange={side => setEditTemp({ ...editTemp, line: { ...editTemp.line, side } })}
          options={[{ value: 'D', label: '借方' }, { value: 'C', label: '貸方' }]} />
        <InputNumber addonBefore="金額" min={0.01} precision={2} style={{ width: '100%' }} value={editTemp.line.amount}
          onChange={amount => setEditTemp({ ...editTemp, line: { ...editTemp.line, amount: amount || 0 } })} />
        <Input addonBefore="摘要" value={editTemp.line.summary} maxLength={150} onChange={e => setEditTemp({ ...editTemp, line: { ...editTemp.line, summary: e.target.value } })} />
      </Space>}
    </Modal>

    <Modal title={formalEditor?.code ? `修改正式傳票 ${formalEditor.code}` : '新增正式傳票'} open={!!formalEditor}
      onCancel={() => setFormalEditor(null)} onOk={saveFormal} okText="儲存" width={1050}>
      {formalEditor && <>
        <Space style={{ marginBottom: 12 }}>
          <DatePicker value={dayjs(formalEditor.voucherDate)} onChange={value => setFormalEditor({ ...formalEditor, voucherDate: value?.format('YYYY-MM-DD') || '' })} />
          <Tag color={Math.round(formalTotals.debit * 100) === Math.round(formalTotals.credit * 100) ? 'green' : 'red'}>
            借 {formalTotals.debit.toFixed(2)}／貸 {formalTotals.credit.toFixed(2)}
          </Tag>
          <Button onClick={() => setFormalEditor({ ...formalEditor, details: [...formalEditor.details, emptyLine()] })}>新增明細</Button>
        </Space>
        <Table rowKey={(_, index) => String(index)} pagination={false} dataSource={formalEditor.details} columns={[
          { title: '部門', render: (_: any, line: SaveVoucherLine, index: number) => <Input value={line.department} maxLength={4} onChange={e => patchFormalLine(index, { department: e.target.value })} /> },
          { title: '科目', render: (_: any, line: SaveVoucherLine, index: number) => <Input value={line.account} maxLength={12} onChange={e => patchFormalLine(index, { account: e.target.value })} /> },
          { title: '稅務科目', render: (_: any, line: SaveVoucherLine, index: number) => <Input value={line.taxAccount} maxLength={12} onChange={e => patchFormalLine(index, { taxAccount: e.target.value })} /> },
          { title: '借貸', width: 90, render: (_: any, line: SaveVoucherLine, index: number) => <Select value={line.side} onChange={side => patchFormalLine(index, { side })} options={[{ value: 'D', label: '借' }, { value: 'C', label: '貸' }]} /> },
          { title: '金額', render: (_: any, line: SaveVoucherLine, index: number) => <InputNumber min={0.01} precision={2} value={line.amount} onChange={amount => patchFormalLine(index, { amount: amount || 0 })} /> },
          { title: '摘要', render: (_: any, line: SaveVoucherLine, index: number) => <Input value={line.summary} maxLength={150} onChange={e => patchFormalLine(index, { summary: e.target.value })} /> },
          { title: '', width: 70, render: (_: any, __: SaveVoucherLine, index: number) => <Button danger size="small" onClick={() => setFormalEditor({ ...formalEditor, details: formalEditor.details.filter((_, i) => i !== index) })}>刪除</Button> },
        ]} />
      </>}
    </Modal>

    <AcceptanceDataPanel
      manifest={chain29VoucherAcceptanceManifest}
      documentKey={acceptanceDocumentKey}
      snapshots={{
        screenDraft: {
          activeTab,
          selectedTemps,
          confirmDate,
          mergeByDate,
          temporaryEdit: editTemp,
          formalDraft: formalEditor,
        },
        lastApiResponse: acceptanceLastApiResponse,
        databaseRecord: acceptanceDatabaseRecord,
      }}
      latestRecords={(activeTab === 'temporary' ? temps : formals).slice(0, 3)}
      history={acceptanceAudit}
      auditEntries={acceptanceAudit}
      observationErrors={acceptanceErrors}
      formulaEvidence={formulaEvidence}
      loading={loading}
      onRefresh={() => {
        if (acceptanceKind === 'formal' && acceptanceDocumentKey) {
          void showFormal({ code: acceptanceDocumentKey } as FormalVoucherHeader);
        } else if (acceptanceKind === 'temporary' && acceptanceDocumentKey) {
          void showTemporary({ number: acceptanceDocumentKey } as TemporaryVoucherHeader);
        }
      }}
    />
  </div>;
};

export default Chain29VoucherForm;
